import React, { useCallback, useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import classNames from "classnames";

import { OpenStatus } from "../OpenLabel";
import { FilterSelect } from "./FilterSelect";

import styles from "./FilterOptions.module.scss";

const STATUSES = [
  {
    label: "Any",
    value: [],
  },
  {
    label: "Open",
    value: [OpenStatus.Open, OpenStatus.Partial],
  },
  {
    label: "Partially Open",
    value: [OpenStatus.Partial],
  },
  {
    label: "Closed",
    value: [OpenStatus.Closed],
  },
];

export type FilterObject = {
  status?: OpenStatus[];
  tag?: string;
};

interface FilterOptionsProps {
  className?: string;
  onFilter: (obj: FilterObject) => void;
}

interface TagsData {
  tags: {
    edges: Array<{
      node: {
        frontmatter: {
          label: string;
        };
      };
    }>;
  };
}

const FilterOptions: React.FC<FilterOptionsProps> = ({
  onFilter,
  className,
}) => {
  const data = useStaticQuery<TagsData>(graphql`
    {
      tags: allMarkdownRemark(
        filter: { fields: { sourceName: { eq: "tags" } } }
      ) {
        edges {
          node {
            frontmatter {
              label
            }
          }
        }
      }
    }
  `);
  const [openStatus, setOpenStatus] = useState(STATUSES[0]);
  const [currentTag, setCurrentTag] = useState<string>();
  const [fullObject, setFullObject] = useState<FilterObject>({
    status: openStatus.value,
    tag: undefined,
  });

  const onStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const status = STATUSES[parseInt(e.target.value)];

      setOpenStatus(status || STATUSES[0]);
    },
    []
  );

  const onTagChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentTag(e.target.value);
  }, []);

  useEffect(() => {
    setFullObject({
      status: openStatus.value,
      tag: currentTag,
    });
  }, [openStatus, currentTag]);

  useEffect(() => {
    onFilter(fullObject);
  }, [fullObject, onFilter]);

  return (
    <div className={classNames(styles.group, className)}>
      <FilterSelect
        className={styles.item}
        title="Status"
        displayLabel={openStatus.label}
        value={openStatus.value}
        onChange={onStatusChange}
      >
        {STATUSES.map((status, idx) => (
          <option key={idx} value={idx}>
            {status.label}
          </option>
        ))}
      </FilterSelect>
      <FilterSelect
        className={styles.item}
        title="Tag"
        displayLabel={currentTag || "Select"}
        value={currentTag}
        onChange={onTagChange}
      >
        <option value="">Any</option>
        {data.tags.edges.map(({ node: tag }, idx) => (
          <option key={tag.frontmatter.label} value={tag.frontmatter.label}>
            {tag.frontmatter.label}
          </option>
        ))}
      </FilterSelect>
    </div>
  );
};

export { FilterOptions };
